import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {useDispatch} from 'react-redux';
import {Container, Form, Grid, Header, Popup, Radio, Button, Icon} from 'semantic-ui-react';
import * as yup from 'yup';

import {getChangedFieldsValues} from 'forms';
import {useReservedHostnames} from 'hooks';
import httpClient from 'httpClient';
import {addSuccessMessage, clearSuccessMessages} from 'state/actions';

import {validationSchemas, siteMinLength, sitesTypeInfo} from '../../Common/sharedElements';

import HostsFormInput from './HostsFormInput';

import styles from './GitlabPages.module.scss';

const defaultFormValues = {
  anonymous: false,
  processedHosts: [],
  gitlabRepositoryUrl: '',
  hosts: [],
};

var regPathSchema = /\[([^)]+)\]/;

const formSchema = yup.object().shape({
  processedHosts: yup.array().of(
    yup.object().shape({
      name: validationSchemas.updateNameValidation
        .test('domainIsUnique', 'Such domain already exists', (value, ctx) => {
          // Get index of the original site name
          const index = regPathSchema.exec(ctx.path)[1];
          const originalSiteName = ctx.from[1].value.hosts[index]?.split('.')[0];

          if (value === originalSiteName) {
            return true;
          }
          const siteDomain = value + ctx.parent.subdomain;
          if (ctx.options.context.reservedHostnames.includes(value)) {
            return ctx.createError({message: 'Provided site name is a reserved name'});
          }
          // Skip the current URLs of the site, since they are reserved because they belong
          // to the site, so check only the new ones
          if (!ctx.from[1].value.hosts.includes(siteDomain)) {
            return !ctx.options.context.reservedFullyQualifiedDomainNames.includes(siteDomain);
          }
          return true;
        })
        .test(
          'minLength',
          `Minimum allowed length of a site name is ${siteMinLength} characters`,
          (value, ctx) => {
            // Get index of the original site name
            const index = regPathSchema.exec(ctx.path)[1];
            const originalSiteName = ctx.from[1].value.hosts[index]?.split('.')[0];

            if (originalSiteName === value) {
              return true;
            }
            return value.length >= siteMinLength;
          }
        ),
      subdomain: yup
        .string()
        .required("Site's subdomain is required")
        .test(
          'domainAlreadySet',
          'You are already using this domain for this site',
          (_value, ctx) => {
            let seen = new Set();
            return !ctx.from[1].value.processedHosts.some(function(currentObject) {
              return seen.size === seen.add(currentObject.name + currentObject.subdomain).size;
            });
          }
        )
        .test(
          'unchangeableSubdomain',
          'This is a special site, please, open a SNoW ticket to request a subdomain change',
          (value, ctx) => {
            // Get index of the original site name if not new
            const index = regPathSchema.exec(ctx.path)[1];
            const originalSiteUrl = ctx.from[1].value.hosts[index];
            const newSiteName = ctx.from[1].value.hosts[index]?.split('.')[0];
            if (originalSiteUrl !== undefined) {
              return !(
                originalSiteUrl.substring(originalSiteUrl.indexOf('.')) !== value &&
                newSiteName.length < siteMinLength
              );
            }
            return true;
          }
        ),
    })
  ),
  gitlabRepositoryUrl: yup
    .string()
    .matches(/^https:\/\/gitlab\.cern\.ch\/([-_A-Za-z0-9]*[-_A-Za-z0-9/]){0,5}$/, {
      message: 'This is not a valid GitLab repository URL',
      excludeEmptyString: true,
    }),
});

const GitlabPagesSiteManagementForm = ({siteName, siteData, projectName, refetch}) => {
  const dispatch = useDispatch();
  const {
    reservedHostnames,
    reservedFullyQualifiedDomainNames,
    isLoading: isLoadingReservedHostnames,
  } = useReservedHostnames('gps');

  const {
    control,
    handleSubmit,
    reset,
    formState: {isDirty, isSubmitting, isValid, dirtyFields, isSubmitSuccessful},
    getValues,
    trigger,
  } = useForm({
    context: {reservedHostnames, reservedFullyQualifiedDomainNames},
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(formSchema),
  });

  const submitHandler = async data => {
    dispatch(clearSuccessMessages());

    let changedFields = getChangedFieldsValues(dirtyFields, data);

    if (dirtyFields['processedHosts']) {
      changedFields['hosts'] = getValues().processedHosts;
      // If processedHosts changed we get all values and replace the key to hosts since it's the BE key
      delete changedFields['processedHosts'];
    }

    if (dirtyFields['gitlabRepositoryUrl'] && changedFields['gitlabRepositoryUrl'].length === 0) {
      changedFields['gitlabRepositoryUrl'] = null;
    }

    await httpClient.patch(`/api/gps/${projectName}/${siteName}`, changedFields);
    dispatch(addSuccessMessage(`The site ${projectName} was updated`));
  };

  useEffect(() => {
    let gpsForm = siteData;

    if (gpsForm) {
      if (gpsForm.gitlabRepositoryUrl === null) {
        gpsForm.gitlabRepositoryUrl = '';
      }

      gpsForm.processedHosts = [];
      gpsForm.hosts.forEach(site => {
        const name = site.substring(0, site.indexOf('.'));
        const subdomain = '.' + site.substring(site.indexOf('.') + 1);
        gpsForm.processedHosts = [
          ...gpsForm.processedHosts,
          {
            name: name,
            subdomain: subdomain,
          },
        ];
      });
    }
    reset(gpsForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteName, siteData, reset]);

  // reset form's values after a successful form submission
  useEffect(() => {
    if (isSubmitSuccessful) {
      refetch();
    }
  }, [isSubmitSuccessful, refetch]);

  return (
    <Container className={styles.GitlabPages}>
      <FormProvider control={control} trigger={trigger} dirtyFields={dirtyFields}>
        <Form id="update-gps-form" onSubmit={handleSubmit(submitHandler)} loading={isSubmitting}>
          <Grid stackable>
            <Grid.Row columns={2}>
              <Grid.Column width={16}>
                <Header as="h4" textAlign="center" dividing>
                  Served From
                </Header>
                <Grid.Row>
                  <HostsFormInput
                    value={getValues().hosts}
                    processedHosts={getValues().processedHosts}
                    name="processedHosts"
                    repositoryUrl={getValues().gitlabRepositoryUrl}
                  />
                </Grid.Row>
                <Grid.Row columns={2} className={styles.GuestAccess}>
                  <Grid>
                    <Grid.Column textAlign="right" width={3}>
                      <Popup
                        className={styles.FixedLabel}
                        content="Enable if you want to allow anonymous access"
                        position="left center"
                        trigger={<label>Guest Access</label>}
                      />
                    </Grid.Column>
                    <Grid.Column textAlign="left" width={7}>
                      <Grid.Row>
                        <Controller
                          control={control}
                          name="anonymous"
                          render={({field: {onChange, value, name}}) => (
                            <Radio
                              toggle
                              name={name}
                              checked={value}
                              onChange={(_evt, data) => onChange(data.checked)}
                              aria-label="Guest Access"
                            />
                          )}
                        />
                      </Grid.Row>
                      <Grid.Row>
                        {getValues().anonymous ? (
                          <p className={styles.GuestAccessMessage}>
                            The site is accessible to unauthenticated users. Toggle this setting if
                            you want to redirect users to the CERN Single Sign-On.
                          </p>
                        ) : (
                          <>
                            <p className={styles.GuestAccessMessage}>
                              The site is accessible only to user groups defined explicitly in the{' '}
                              <a
                                href="https://application-portal.web.cern.ch/"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Application Portal
                              </a>
                              . These are set by default to all CERN account holders and EduGAIN
                              users, but can be easily changed.
                            </p>
                            <p className={styles.GuestAccessMessage}>
                              <a
                                href={`${sitesTypeInfo.gps.url}/advanced/restricted_egroups/`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Learn more
                              </a>
                            </p>
                          </>
                        )}
                      </Grid.Row>
                    </Grid.Column>
                  </Grid>
                </Grid.Row>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2} className={styles.Repository}>
              <Grid.Column width={16}>
                <Header as="h4" textAlign="center" dividing>
                  Repository
                </Header>
                <Grid.Row>
                  The following input accepts a URL that should correspond to the repository where
                  the static files for your site are stored. This is purely informative and doesn't
                  affect your page in any way. In case the repository is changed, we encourage you
                  to update this field to reflect the change.
                </Grid.Row>
                <Grid.Row className={styles.GitLabInput}>
                  <Grid>
                    <Grid.Column textAlign="left" width={3}>
                      <div className={styles.FixedLabel}>
                        <Popup
                          content="GitLab repository that contains the files for your static site"
                          position="left center"
                          trigger={
                            <label>
                              <Icon name="info" /> GitLab repository
                            </label>
                          }
                        />
                      </div>
                    </Grid.Column>
                    <Grid.Column width={13}>
                      <Grid.Row>
                        <Controller
                          control={control}
                          name="gitlabRepositoryUrl"
                          render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <Form.Input
                              name={name}
                              value={value}
                              error={error?.message}
                              onChange={(_evt, data) => {
                                onChange(data.value);
                              }}
                              aria-label="GitLab repository"
                            />
                          )}
                        />
                      </Grid.Row>
                    </Grid.Column>
                  </Grid>
                </Grid.Row>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column textAlign="right">
                <Button
                  type="submit"
                  className={styles.SaveButton}
                  disabled={!isDirty || isLoadingReservedHostnames || !isValid}
                >
                  Save
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </FormProvider>
    </Container>
  );
};

GitlabPagesSiteManagementForm.propTypes = {
  siteName: PropTypes.string.isRequired,
  siteData: PropTypes.object.isRequired,
  projectName: PropTypes.string.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default GitlabPagesSiteManagementForm;
